import type { NavigationItem } from '../types/navigation.types';
import { QUALITY_RECORDS_ROOT_CODE } from '../navigation/const';

export interface PathBuilderConfig {
  categoryInfo?: any;
  parentId?: string | null;
  hierarchy?: NavigationItem[];
}

export function useGenericPathBuilder() {
  const buildPathFromItem = (item: NavigationItem, parentPath: string[] = []): string[] => {
    const path = [...parentPath];
    
    if (item.code) {
      path.push(item.code);
    } else if (item.name) {
      path.push(item.name);
    } else {
      path.push(undefined as any);
    }
    
    return path;
  };

  const findParentInHierarchy = (item: NavigationItem, hierarchy: NavigationItem[]): NavigationItem | null => {
    for (const category of hierarchy) {
      const found = findParentRecursive(category, item);
      if (found) return found;
    }
    return null;
  };

  const findParentRecursive = (current: NavigationItem, target: NavigationItem): NavigationItem | null => {
    if (current.children) {
      for (const child of current.children) {
        if (child.id === target.id) {
          return current;
        }
        const found = findParentRecursive(child, target);
        if (found) return found;
      }
    }
    return null;
  };

  const buildPathFromHierarchy = (item: NavigationItem, hierarchy: NavigationItem[]): string[] => {
    const path: string[] = [];
    let currentItem: NavigationItem | null = item;
    
    if (currentItem.code) {
      path.unshift(currentItem.code);
    }
    
    while (currentItem) {
      const parent = findParentInHierarchy(currentItem, hierarchy);
      if (parent?.code) {
        path.unshift(parent.code);
      }
      currentItem = parent;
    }
    
    return path;
  };

  const buildPathWithConfig = (item: NavigationItem, config: PathBuilderConfig = {}): string[] => {
    const { parentId, hierarchy } = config;
    
    if (hierarchy) {
      return buildPathFromHierarchy(item, hierarchy);
    }
    
    if (parentId) {
      const parentPath = parentId.split('-').filter(Boolean);
      return buildPathFromItem(item, parentPath);
    }
    
    return buildPathFromItem(item);
  };

  const shouldNavigate = (item: NavigationItem, hasChildren: boolean): boolean => {
    if (!hasChildren) {
      // The Registros da Qualidade root has no listing of its own: with no matrix under it, it is a
      // leaf that must still not open one.
      return item.code !== QUALITY_RECORDS_ROOT_CODE;
    }
    
    return item.type === 'module' || (item.code?.startsWith('M') ?? false);
  };

  return {
    buildPathFromItem,
    buildPathFromHierarchy,
    buildPathWithConfig,
    shouldNavigate,
    findParentInHierarchy
  };
} 